import { useState } from "react";
import { Gift, TrendingUp } from "lucide-react";
import { BonusStatusBadge } from "@/components/BonusFormDialog";
import { CommissionStatusBadge } from "@/components/CommissionFormDialog";
import { EarningsSection } from "./sections/EarningsSection";
import { EarningsDetailDialog } from "./sections/EarningsDetailDialog";
import {
  WorkwearSection,
  WorkwearDetailDialog,
} from "./sections/Workwear";
import {
  DisciplinarySection,
  DisciplinaryDetailDialog,
} from "./sections/Disciplinary";

export function OperationalTab({
  bonuses,
  bonusesLoading,
  approvingBonusId,
  rejectingBonusId,
  onCreateBonus,
  onApproveBonus,
  onRejectBonus,
  onDeleteBonus,

  commissions,
  commissionsLoading,
  approvingCommissionId,
  rejectingCommissionId,
  onCreateCommission,
  onApproveCommission,
  onRejectCommission,
  onDeleteCommission,

  workwear,
  workwearLoading,
  returningWorkwearId,
  onCreateWorkwear,
  onReturnWorkwear,
  onDeleteWorkwear,

  disciplinaries,
  disciplinariesLoading,
  onCreateDisciplinary,
  onDeleteDisciplinary,
}) {
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [selectedWorkwear, setSelectedWorkwear] = useState(null);
  const [selectedDisciplinary, setSelectedDisciplinary] = useState(null);

  const liveBonus = selectedBonus
    ? bonuses.find((b) => b._id === selectedBonus._id) || selectedBonus
    : null;
  const liveCommission = selectedCommission
    ? commissions.find((c) => c._id === selectedCommission._id) || selectedCommission
    : null;
  const liveWorkwear = selectedWorkwear
    ? workwear.find((w) => w._id === selectedWorkwear._id) || selectedWorkwear
    : null;
  const liveDisciplinary = selectedDisciplinary
    ? disciplinaries.find((d) => d._id === selectedDisciplinary._id) ||
      selectedDisciplinary
    : null;

  return (
    <div className="divide-y divide-border">
      <div className="pb-10">
        <EarningsSection
          items={bonuses}
          loading={bonusesLoading}
          onCreate={onCreateBonus}
          onDelete={onDeleteBonus}
          onSelect={setSelectedBonus}
          StatusBadge={BonusStatusBadge}
          Icon={Gift}
          testIdPrefix="bonus"
          labels={{
            eyebrow: "Bónusz",
            sectionTitle: "Jóváírt bruttó bónuszok",
            newButton: "Új bónusz",
            itemNoun: "bónusz",
            emptyHeader: "Még nincs rögzített bónusz.",
            emptyTitle: "Nincs rögzített bónusz",
            emptyDescription: "Rögzítsd a dolgozó jóváírt bruttó bónuszait.",
            loadingText: "Bónuszok betöltése…",
            menuAriaLabel: "Bónusz műveletek",
          }}
        />
      </div>

      <div className="pt-10">
        <EarningsSection
          items={commissions}
          loading={commissionsLoading}
          onCreate={onCreateCommission}
          onDelete={onDeleteCommission}
          onSelect={setSelectedCommission}
          StatusBadge={CommissionStatusBadge}
          Icon={TrendingUp}
          testIdPrefix="commission"
          labels={{
            eyebrow: "Jutalék",
            sectionTitle: "Jóváírt bruttó jutalékok",
            newButton: "Új jutalék",
            itemNoun: "jutalék",
            emptyHeader: "Még nincs rögzített jutalék.",
            emptyTitle: "Nincs rögzített jutalék",
            emptyDescription:
              "Rögzítsd a dolgozó jóváírt bruttó jutalékait (eladás, projekt, stb.).",
            loadingText: "Jutalékok betöltése…",
            menuAriaLabel: "Jutalék műveletek",
          }}
        />
      </div>

      <div className="pt-10">
        <WorkwearSection
          items={workwear}
          loading={workwearLoading}
          onCreate={onCreateWorkwear}
          onSelect={setSelectedWorkwear}
          onDelete={onDeleteWorkwear}
        />
      </div>

      <div className="pt-10">
        <DisciplinarySection
          items={disciplinaries}
          loading={disciplinariesLoading}
          onCreate={onCreateDisciplinary}
          onSelect={setSelectedDisciplinary}
          onDelete={onDeleteDisciplinary}
        />
      </div>

      <EarningsDetailDialog
        open={!!liveBonus}
        onOpenChange={(o) => !o && setSelectedBonus(null)}
        item={liveBonus}
        StatusBadge={BonusStatusBadge}
        testIdPrefix="bonus"
        approving={approvingBonusId === liveBonus?._id}
        rejecting={rejectingBonusId === liveBonus?._id}
        onApprove={onApproveBonus}
        onReject={onRejectBonus}
        onDelete={(item) => {
          setSelectedBonus(null);
          onDeleteBonus(item);
        }}
        labels={{
          detailTitle: "Bónusz részletei",
          detailDescription:
            "A bónusz teljes információja. Jóváhagyhatod vagy elutasíthatod itt.",
        }}
      />

      <EarningsDetailDialog
        open={!!liveCommission}
        onOpenChange={(o) => !o && setSelectedCommission(null)}
        item={liveCommission}
        StatusBadge={CommissionStatusBadge}
        testIdPrefix="commission"
        approving={approvingCommissionId === liveCommission?._id}
        rejecting={rejectingCommissionId === liveCommission?._id}
        onApprove={onApproveCommission}
        onReject={onRejectCommission}
        onDelete={(item) => {
          setSelectedCommission(null);
          onDeleteCommission(item);
        }}
        labels={{
          detailTitle: "Jutalék részletei",
          detailDescription:
            "A jutalék teljes információja. Jóváhagyhatod vagy elutasíthatod itt.",
        }}
      />

      <WorkwearDetailDialog
        open={!!liveWorkwear}
        onOpenChange={(o) => !o && setSelectedWorkwear(null)}
        item={liveWorkwear}
        returning={returningWorkwearId === liveWorkwear?._id}
        onReturn={onReturnWorkwear}
        onDelete={(item) => {
          setSelectedWorkwear(null);
          onDeleteWorkwear(item);
        }}
      />

      <DisciplinaryDetailDialog
        open={!!liveDisciplinary}
        onOpenChange={(o) => !o && setSelectedDisciplinary(null)}
        item={liveDisciplinary}
        onDelete={(item) => {
          setSelectedDisciplinary(null);
          onDeleteDisciplinary(item);
        }}
      />
    </div>
  );
}
